import * as Sentry from '@sentry/browser';
import { Primitive } from '@sentry/types';
import _ from 'lodash';
import cond from 'lodash/fp/cond';
import constant from 'lodash/fp/constant';
import matches from 'lodash/fp/matches';
import noop from 'lodash/fp/noop';
import otherwise from 'lodash/fp/stubTrue';

import ApiError from '../api/apiError';
import { configureEnv } from '../env';

const EXPECTED_HTTP_ERROR_CODES = new Set([401, 403]);

export function isExpectedHttpError(error: unknown): boolean {
    return error instanceof ApiError && EXPECTED_HTTP_ERROR_CODES.has(error.errorCode);
}

export type SentryErrorInfo = {
    tags?: Record<string, Primitive>;
} & Record<string, unknown>;

export function configureReportingGlobal(tags: Record<string, Primitive>, injectedSentry = Sentry) {
    injectedSentry.configureScope(function (scope) {
        if (!_.isEmpty(tags)) {
            scope.setTags(tags);
        }
    });
}

export function configureReportingUser(
    user: {
        sub?: string;
        email?: string;
        name?: string;
        username?: string;
        account?: string;
        locale?: string;
        azp?: string;
        client_id?: string;
        tenant?: string;
    },
    injectedSentry = Sentry,
) {
    if (!_.isEmpty(user)) {
        injectedSentry.setUser(user);
    }
}

export function configureReporting(
    injectedWindow = window,
    importMetaEnv: ImportMetaEnv,
    injectedSentry = Sentry,
) {
    const configuredEnv = configureEnv(injectedWindow, importMetaEnv || import.meta.env);

    const {
        featureToggles,
        isProdBuild,
        isRunningOnProd,
        isRunningOnStaging,
        runsInSandbox,
        isTestEnv,
    } = configuredEnv;

    const console =
        typeof injectedWindow['console'] !== 'undefined' ? injectedWindow.console : null;

    const trace = featureToggles.tracing
        ? (...args: unknown[]) => console?.log('[sentryReporting]', ...args)
        : () => {};

    trace('CONFIGURE_REPORTING', {
        host: injectedWindow.location?.host,
        isProdBuild,
        isRunningOnProd,
        isRunningOnStaging,
        runsInSandbox,
        isTestEnv,
    });

    const hasConsole = !!console;

    const justLogException =
        hasConsole && !isTestEnv
            ? (e: Error, errorInfo = '(no additional info)') => {
                  trace('JUST_LOG_EXCEPTION', {
                      error: e,
                      isRunningOnProd,
                      isRunningOnStaging,
                      runsInSandbox,
                  });

                  console?.error('Exception captured:', e, errorInfo);
              }
            : noop;

    const reportWithSentry =
        injectedSentry && injectedSentry.withScope
            ? (e: Error, errorInfo: SentryErrorInfo = {}) => {
                  trace('REPORT_WITH_SENTRY', {
                      error: e,
                      isRunningOnProd,
                      isRunningOnStaging,
                      runsInSandbox,
                  });

                  return injectedSentry.withScope((scope) => {
                      const { tags, ...extras } = errorInfo;

                      if (!_.isEmpty(tags)) {
                          scope.setTags(tags);
                      }

                      if (!_.isEmpty(extras)) {
                          scope.setExtras(extras);
                      }

                      injectedSentry.captureException(e as Error);
                  });
              }
            : noop;

    const logToConsoleAndReport = (e: Error, errorInfo?: string | Record<string, unknown>) => {
        trace('LOG_TO_CONSOLE_AND_REPORT', {
            error: e,
            isRunningOnProd,
            isRunningOnStaging,
            runsInSandbox,
        });

        justLogException(e, errorInfo as string);
        reportWithSentry(e, errorInfo as SentryErrorInfo);
    };

    const state = {
        isProdBuild,
        isRunningOnProd,
        isRunningOnStaging,
        runsInSandbox,
    };

    trace('STATE', state);

    const captureException = cond([
        [matches({ isRunningOnProd: true }), constant(reportWithSentry)],
        [matches({ isRunningOnStaging: true }), constant(reportWithSentry)],
        [matches({ runsInSandbox: true }), constant(logToConsoleAndReport)],
        [otherwise, constant(justLogException)],
    ])(state);

    const captureSagaCancelledMiddleware = (/* store */) =>
        (next: (val: Record<string, unknown>) => void) =>
            cond([
                [
                    matches({ type: 'report/SAGA_CANCELLED' }),
                    ({ payload }: { payload: Error }) => captureException(payload),
                ],
                [otherwise, next],
            ]);

    return {
        captureException,
        captureSagaCancelledMiddleware,
    };
}
